/**
 * Regex patterns for errors that Sentry should silently drop.
 *
 * These are passed to `ignoreErrors` in the Sentry.init() call. Each pattern
 * targets a known class of noise that originates outside our application code
 * (browser extensions, third-party injected scripts, etc.).
 */
export const SENTRY_IGNORE_PATTERNS: Array<string | RegExp> = [
  // Browser extensions use chrome.runtime.sendMessage / browser.runtime.sendMessage
  // to communicate between content scripts and background pages. When a tab is
  // closed mid-message, the browser surfaces this error globally -- it has no
  // in-app stack frames and is completely outside our control.
  /Invalid call to runtime\.sendMessage\(\)/,

  // @rerun-io/web-viewer WASM throws this when polling code queries a viewer
  // whose internal state has transitioned to stopped (e.g. gRPC stream ended).
  // The try/catch in RerunViewerHandle methods handles this gracefully; this
  // pattern catches any that propagate as unhandled rejections.
  /in a stopped web viewer/,

  // wasm-bindgen closure teardown errors from @rerun-io/web-viewer: after
  // viewer.stop() frees the WASM module, pending async callbacks invoke freed
  // closures. Naming varies by wasm-bindgen version (externref_shim vs
  // wasm_bindgen__convert__closures).
  /wasm_bindgen__convert__closures/,

  // KernelNotFoundError is thrown when a consumer attempts to attach to a
  // kernel that has died (crash, shutdown, OOM) between the moment the UI
  // cached the kernel list and the moment of the click. The KernelPool
  // self-heals by refreshing, and the KernelSelector surfaces a toast.
  // Never an application bug worth paging on.
  /Kernel '[^']+' not found/,

  // @jupyterlab/services cancels all pending kernel futures (including
  // internal kernel_info_request messages not awaited by application code)
  // when a kernel connection is disposed via _clearKernelState(). This
  // happens during kernel restart, auto-restart after crash, watchdog
  // teardown, workspace switching, and page unload. The unhandledrejection
  // handler in main.tsx calls preventDefault() but Sentry's GlobalHandlers
  // integration captures the event before checking defaultPrevented, so
  // ignoreErrors is needed as a second layer of defense.
  /Canceled future for .+ message before replies were done/,

  // @jupyterlab/services rejects a pending kernel.reconnect() promise with
  // "Kernel connection disconnected" when the kernel is disposed before
  // reconnection completes. This happens during the graceful-restart-then-
  // force-restart fallback (Promise.race timeout abandons the in-flight
  // restart), and during workspace teardowns where the sim returns 401/503
  // for the kernel API. Same defense-in-depth rationale as canceled futures.
  /Kernel connection disconnected/,

  // Auth0 SPA SDK throws these OAuth-shaped errors from inside its silent-auth
  // iframe postMessage handler when the user's SSO session has expired or was
  // revoked, when no refresh token is available, or when a refresh token is
  // rejected. AuthSessionRecoveryProvider already catches these via
  // isRecoverableAuthSessionError and forces a clean re-login -- they are
  // expected, recoverable behavior, not application bugs. They reach Sentry
  // because the throw happens inside the Auth0 iframe's wrapped postMessage
  // handler, which Sentry's GlobalHandlers integration captures before our
  // catch runs.
  /^Login required$/,
  /^Missing Refresh Token/i,
  /^Invalid Grant$/i,
];
